import { router } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"

import {
  applyChoice,
  gateCookieName,
  gpcEnabled,
  readGateChoice,
  sweepOldCookies,
  writeGateChoice,
} from "@/lib/consent"

// The gate blocks the whole site, so it must NOT render on the legal pages it
// links to — otherwise a not-yet-confirmed user can never reach Privacy Policy,
// Terms, or Your Privacy Choices before agreeing. GunBuyBack does this with a
// Blade `if` evaluated per request; Firearmland is an Inertia SPA, so it has to
// be re-evaluated on client-side navigation too — hence the router listener.
const EXEMPT_PATHS = ["/privacy", "/terms", "/privacy-choices"]

function normalisePath(pathname) {
  const path = pathname.replace(/\/+$/, "")
  return path === "" ? "/" : path
}

/**
 * First-visit modal: an 18+ confirmation fused with an opt-in privacy consent.
 * One "Continue" is the opt-in; the choice can be changed later via the footer
 * "Your Privacy Choices" link.
 *
 * Mounted globally from app.jsx rather than inside a layout, so it covers every
 * page regardless of which layout that page uses. Renders null until the client
 * effect runs, which keeps it inert during SSR and hydration.
 */
export default function PrivacyGate() {
  const [open, setOpen] = useState(false)
  const [gpc, setGpc] = useState(false)
  const [path, setPath] = useState(null)
  const resolved = useRef(false)

  useEffect(() => {
    // Resolve the stored choice exactly once per full page load — not on every
    // Inertia navigation, or a returning visitor would re-send their consent to
    // the CMP on every click.
    if (resolved.current) return
    resolved.current = true

    setPath(normalisePath(window.location.pathname))
    setGpc(gpcEnabled())
    sweepOldCookies(gateCookieName())

    const choice = readGateChoice()
    if (choice) {
      // Returning visitor who already passed: skip the UI, but re-assert their
      // stored choice with the CMP (GPC still forces opt-out regardless).
      applyChoice(choice === "in")
      return
    }

    setOpen(true)
  }, [])

  useEffect(() => {
    return router.on("navigate", () => {
      setPath(normalisePath(window.location.pathname))
    })
  }, [])

  const exempt = path === null || EXEMPT_PATHS.includes(path)

  // Scroll lock goes on <html>, NOT <body>, and as an inline style rather than a
  // utility class. Header.jsx (and several other components) write
  // `document.body.style.overflow` directly on mount -- Header sets "auto"
  // unconditionally because its drawer starts closed. An inline style always
  // beats a class, so a `body.overflow-hidden` lock is silently a no-op: the
  // class lands on <body> but the page still scrolls behind the gate.
  // <html> is document.scrollingElement and nothing else touches its inline
  // style, so locking there sidesteps that contention entirely.
  useEffect(() => {
    if (!(open && !exempt)) return

    const html = document.documentElement
    const previous = html.style.getPropertyValue("overflow")
    const previousPriority = html.style.getPropertyPriority("overflow")
    html.style.setProperty("overflow", "hidden", "important")

    return () => {
      if (previous) {
        html.style.setProperty("overflow", previous, previousPriority)
      } else {
        html.style.removeProperty("overflow")
      }
    }
  }, [open, exempt])

  function handleContinue() {
    // GPC wins: visitors sending the signal start (and stay) opted out.
    const optedIn = !gpcEnabled()
    applyChoice(optedIn)
    writeGateChoice(optedIn)
    setOpen(false)
  }

  function handleLeave() {
    // Under 18: leave immediately, no cookie, no back-button history.
    window.location.replace("https://www.google.com")
  }

  if (!open || exempt) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-gate-title"
      aria-describedby="privacy-gate-notice"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/70" aria-hidden="true" />

      <div className="relative flex w-full max-w-lg flex-col items-center gap-4 rounded-lg bg-white p-6 shadow-xl md:p-8">
        <img
          src="/images/logo.webp"
          alt="FirearmLand"
          className="w-auto h-10"
          width="160"
          height="40"
        />

        <h2
          id="privacy-gate-title"
          className="text-lg font-semibold text-center md:text-xl"
        >
          Age Verification &amp; Terms
        </h2>

        <p id="privacy-gate-notice" className="text-sm text-center text-gray-600">
          You must be 18 or older to use this site. We don&rsquo;t use any
          non-essential cookies or tracking until you continue. By clicking
          Continue, you confirm you&rsquo;re at least 18 and agree to our{" "}
          <a href="/terms" target="_blank" rel="noopener" className="underline">
            Terms and Conditions
          </a>{" "}
          and{" "}
          <a href="/privacy" target="_blank" rel="noopener" className="underline">
            Privacy Policy
          </a>
          , and consent to cookies and similar technologies as described in our
          Privacy Policy. You can change or withdraw your choice anytime through
          &ldquo;Your Privacy Choices&rdquo; in the footer.
        </p>

        {gpc && (
          <div
            role="status"
            className="w-full p-3 text-xs text-gray-700 rounded-md bg-gray-50"
          >
            Opt-Out Request Honored. Your browser sent a Global Privacy Control
            signal, so we have started you opted out of the sharing of your
            personal information.
          </div>
        )}

        <button
          type="button"
          onClick={handleContinue}
          className="w-full px-6 py-3 text-sm font-semibold text-white transition bg-black rounded-md hover:bg-gray-800 focus:outline-none"
        >
          Continue
        </button>

        <button
          type="button"
          onClick={handleLeave}
          className="text-sm text-gray-500 underline transition hover:text-gray-700"
        >
          Leave
        </button>
      </div>
    </div>
  )
}
